// Deep-link signals for in-app navigation to a specific task detail.
//
// A signal (not the URL query string) is used deliberately: wouter's useLocation
// exposes only the pathname, so a `?task=` change on the SAME path never
// re-renders — clicking a result for the project/list you are already viewing
// would silently do nothing. Signals are reactive regardless of the path, so the
// target view opens the detail every time. Set by the command palette, search
// results, or a step row; consumed + cleared by Project/Lists.
//
// This lives in its own leaf module (only depends on @preact/signals) so that
// light-weight consumers — e.g. a task row that wants to open a step — don't have
// to import the heavy CommandPalette component just to reach the signal.

// Der zweite Weg in dieselbe Ansicht ist die URL: ein geteilter Link oder eine
// Benachrichtigung, die einen echten Seitenaufruf auslöst, trägt die Aufgabe als
// `?task=<id>`. Signal und Query-Parameter ergänzen sich also — das Signal für
// In-App-Navigation ohne Remount, der Parameter für frische Seitenaufrufe.
// parseAndStripTaskParam() unten kapselt das Auslesen für beide Routen.

import { signal } from "@preact/signals";

export const openProjectTaskSig = signal<number | null>(null);
export const openPersonalTaskSig = signal<number | null>(null);

/**
 * Liest `?task=<id>` aus der aktuellen URL und entfernt den Parameter wieder —
 * einmal konsumieren, damit eine manuell geschlossene Aufgabe beim nächsten
 * Rendern nicht erneut aufgeht. Restliche Query-Parameter und der Hash bleiben
 * erhalten. Gibt die id zurück, oder null wenn kein oder kein numerischer
 * Parameter da ist; in dem Fall wird die URL auch nicht angefasst.
 * Genutzt von Project.tsx (`/p/:id`) und Lists.tsx (`/lists/:id?`), die jeweils
 * ihren eigenen useEffect mit eigener Dependency behalten.
 */
export function parseAndStripTaskParam(): number | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("task");
  if (!raw) return null;
  const taskId = Number.parseInt(raw, 10);
  if (Number.isNaN(taskId)) return null;
  params.delete("task");
  const query = params.toString();
  const next = window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
  window.history.replaceState({}, "", next);
  return taskId;
}
